<script setup>
import axios from 'axios';
import { BCard, BFormSelect, BFormCheckbox } from 'bootstrap-vue-next';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';
import { booleanParam } from '~/common/QueryParams';
import { useSeriesChartQuery } from '~/common/useSeriesChartQuery';

import DashboardChart from './dashboard-page/DashboardChart.vue';

const data = ref([]);
const error = ref(null);
const chartRef = ref(null);

const route = useRoute();

const MERGE_NAMESPACE_SERIES = ['merge_gitlab_org', 'merge_gitlab_com', 'merge_gitlab_community', 'merge_components', 'merge_other'];
const OTHER_SERIES = ['label', 'close', 'translate', 'discord', 'forum', 'notes', 'influenced_closures', 'ai_catalog'];

// Parsed standalone and passed in as an extra ref because series' allowed/fallback
// depend on allSeries, which depends on splitMerge, so splitMerge must exist first.
const splitMergeParam = booleanParam(false);
const splitMerge = ref(splitMergeParam.parse(route.query.splitMerge));

const allSeries = computed(() => (
  splitMerge.value ? [...MERGE_NAMESPACE_SERIES, ...OTHER_SERIES] : ['merge', ...OTHER_SERIES]
));

const {
  period,
  chartType,
  stacked,
  unique,
  activeSeries,
  updateQueryString,
  updateActiveSeries,
  hideInactiveSeries,
} = useSeriesChartQuery({
  allSeries,
  extraParams: { splitMerge: { param: splitMergeParam, ref: splitMerge } },
});

const fetchData = async () => {
  const url = '/api/v1/dashboard';

  error.value = null;
  let response;

  try {
    response = await axios.get(url, {
      params: {
        period: period.value,
        unique: unique.value,
      },
    });
  } catch (e) {
    error.value = e;
  }

  if (response?.status === 200) {
    data.value = await response.data;
  }
};

const graphDates = computed(() => data.value?.map(activity => activity.activity_date));

const graphPoints = computed(() => {
  if (!graphDates.value?.length) {
    return [];
  }

  const points = [];
  for (const date of graphDates.value) {
    const dayData = data.value.find(activity => activity.activity_date === date);
    points.push({
      translate: dayData?.translated_strings || 0,
      label: dayData?.added_labels || 0,
      close: dayData?.closed_issues || 0,
      merge: dayData?.merged_merge_requests || 0,
      merge_gitlab_org: dayData?.merged_gitlab_org || 0,
      merge_gitlab_com: dayData?.merged_gitlab_com || 0,
      merge_gitlab_community: dayData?.merged_gitlab_community || 0,
      merge_components: dayData?.merged_components || 0,
      merge_other: dayData?.merged_other || 0,
      discord: (dayData?.discord_messages || 0) + (dayData?.discord_replies || 0),
      forum: (dayData?.forum_posts || 0) + (dayData?.forum_replies || 0),
      notes: dayData?.added_notes || 0,
      influenced_closures: dayData?.influenced_closures || 0,
      ai_catalog: dayData?.ai_contributions || 0,
    });
  }

  return points;
});

watch([period, unique], () => {
  fetchData();
  updateQueryString();
});

watch(splitMerge, (isSplit, wasSplit) => {
  const previousMergeSeries = wasSplit ? MERGE_NAMESPACE_SERIES : ['merge'];
  const nextMergeSeries = isSplit ? MERGE_NAMESPACE_SERIES : ['merge'];
  const mergeGroupWasActive = previousMergeSeries.some(series => activeSeries.value.includes(series));
  const unrelatedActiveSeries = activeSeries.value.filter(series => !previousMergeSeries.includes(series));

  activeSeries.value = mergeGroupWasActive
    ? [...unrelatedActiveSeries, ...nextMergeSeries]
    : unrelatedActiveSeries;

  updateQueryString();
});

watch([data, chartRef, chartType, allSeries], () => {
  if (data.value.length && chartRef.value) {
    hideInactiveSeries(chartRef.value);
  }
}, { flush: 'post' });

fetchData();
</script>

<template>
  <ErrorState v-if="error" />
  <div v-else>
    <BCard class="mb-4">
      <div class="d-flex flex-wrap gap-4 align-items-center">
        <div class="d-flex align-items-center">
          <label class="form-label me-2 mb-0">Period:</label>
          <BFormSelect
            v-model="period"
            size="sm"
            class="select-sm-fixed"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </BFormSelect>
        </div>

        <div class="d-flex align-items-center">
          <label class="form-label me-2 mb-0">Chart Type:</label>
          <BFormSelect
            v-model="chartType"
            size="sm"
            class="select-sm-fixed"
          >
            <option value="line">Line</option>
            <option value="bar">Bar</option>
          </BFormSelect>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="stacked"
            :unchecked-value="false"
            data-testid="stacked-checkbox"
            class="mb-0"
          >
            Stacked
          </BFormCheckbox>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="unique"
            :unchecked-value="false"
            data-testid="unique-checkbox"
            class="mb-0"
          >
            Unique
          </BFormCheckbox>
        </div>

        <div class="d-flex align-items-center">
          <BFormCheckbox
            v-model="splitMerge"
            :unchecked-value="false"
            class="mb-0"
          >
            Split merge by namespace
          </BFormCheckbox>
        </div>
      </div>
    </BCard>

    <DashboardChart
      v-if="data.length"
      :key="splitMerge"
      ref="chartRef"
      :graph-points="graphPoints"
      :graph-dates="graphDates"
      :chart-type="chartType"
      :stacked="stacked"
      :all-series="allSeries"
      @update:active-series="updateActiveSeries"
    />
  </div>
</template>
